import {
  BRAND_COLOR_KEYS,
  computeBrandTokens,
  getContrastRatio,
  getLuminance,
  MIN_TEXT_CONTRAST,
  PARTICIPANT_HUB_LIGHT_TOKENS,
  PARTICIPANT_HUB_SOLID_TEXT,
} from '@hub/common';
import type { BrandColorKey, BrandInputs } from '@hub/common';

export type BrandField =
  BrandColorKey | 'borderRadius' | 'label' | 'sponsorName';
/** The form's input: raw strings as typed, empty = default. `logo` is a data URL until publish. */
export type BrandDraft = Partial<Record<BrandField, string>> & {
  logo?: string;
};
export type BrandCheck = {
  errors: Partial<Record<BrandField, string>>;
  warnings: Partial<Record<BrandColorKey, string>>;
};

export const BRAND_COLOR_LABELS: Record<BrandColorKey, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  background: 'Page background',
  surface: 'Surface',
  text: 'Text',
};

export const HEX_COLOR = /^#[0-9a-f]{6}$/i;
export const readBlobAsDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('Unable to read the logo'));
    };
    reader.readAsDataURL(blob);
  });

export const BRAND_LOGO_TYPES = {
  'image/png': 'png',
  'image/webp': 'webp',
} as const;

/** Downloads a logo and returns it as a PNG or WebP blob with its file
 *  extension. */
export const fetchBrandLogoBlob = async (
  url: string
): Promise<{ blob: Blob; extension: string }> => {
  const response = await fetch(url);
  const blob = await response.blob();
  if (!response.ok || !Object.hasOwn(BRAND_LOGO_TYPES, blob.type)) {
    throw new Error('The logo must be a PNG or WebP image');
  }
  return {
    blob,
    extension: BRAND_LOGO_TYPES[blob.type as keyof typeof BRAND_LOGO_TYPES],
  };
};

/** A published brand's logo as a data URL, for a draft. */
export const readBrandLogoAsDataUrl = async (logoUrl: string) =>
  readBlobAsDataUrl((await fetchBrandLogoBlob(logoUrl)).blob);

export const BRAND_LOGO_MAX_BYTES = 200 * 1024;

export const validateBrandLogo = (
  type: string,
  size: number
): string | undefined => {
  if (!Object.hasOwn(BRAND_LOGO_TYPES, type)) {
    return 'Use a PNG or WebP image';
  }
  if (size > BRAND_LOGO_MAX_BYTES) {
    return 'Use an image of at most 200 KB';
  }
  return undefined;
};

export const validateBrandLogoFile = (file: File): string | undefined =>
  validateBrandLogo(file.type, file.size);

export const MAX_RADIUS = 24;
const GRAPHIC_MIN_CONTRAST = 3;

/** Participant Hub light colours as solid values, for the colour pickers and
 *  contrast checks. */
export const PARTICIPANT_HUB_SOLID_COLORS: Record<BrandColorKey, string> = {
  primary: PARTICIPANT_HUB_LIGHT_TOKENS.primary,
  secondary: PARTICIPANT_HUB_LIGHT_TOKENS.secondary,
  background: PARTICIPANT_HUB_LIGHT_TOKENS.background,
  surface: PARTICIPANT_HUB_LIGHT_TOKENS.surface,
  text: PARTICIPANT_HUB_SOLID_TEXT,
};

export const HEX_COLOR_ERROR = 'Use a hex color like #3f51b5';
const DARK_BRAND_ERROR =
  'Branded pages are light: use a light background and surface';
const LIGHT_TEXT_ERROR = 'Branded pages are light: use a dark text colour';
export const RADIUS_ERROR = `Use a whole number from 0 to ${MAX_RADIUS.toString()}`;

export const parseHexColor = (
  value: string | undefined
): string | undefined => {
  const trimmed = value?.trim();
  return trimmed && HEX_COLOR.test(trimmed) ? trimmed.toLowerCase() : undefined;
};

const parseRadius = (value: string | undefined): number | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  const radius = Number(trimmed);
  return Number.isInteger(radius) && radius >= 0 && radius <= MAX_RADIUS
    ? radius
    : undefined;
};

/** The draft's valid values as `BrandInputs`, and a format error for each
 *  missing label or sponsor name and each malformed colour or radius. */
export const parseBrandDraft = (
  draft: BrandDraft
): { inputs: BrandInputs; errors: BrandCheck['errors'] } => {
  const errors: BrandCheck['errors'] = {};
  if (!draft.label?.trim()) errors.label = 'Enter a label';
  if (!draft.sponsorName?.trim()) {
    errors.sponsorName = 'Enter the sponsor name';
  }
  const colors: BrandInputs['colors'] = {};
  for (const key of BRAND_COLOR_KEYS) {
    if (!draft[key]?.trim()) continue;
    const color = parseHexColor(draft[key]);
    if (color) colors[key] = color;
    else errors[key] = HEX_COLOR_ERROR;
  }
  if (!draft.borderRadius?.trim()) return { inputs: { colors }, errors };
  const radius = parseRadius(draft.borderRadius);
  if (radius === undefined) {
    errors.borderRadius = RADIUS_ERROR;
    return { inputs: { colors }, errors };
  }
  return { inputs: { colors, borderRadius: radius }, errors };
};

/** A draft holding a brand's label, sponsor name and inputs. */
export const toBrandDraft = ({
  label,
  sponsorName,
  inputs,
}: {
  label: string;
  sponsorName: string;
  inputs: BrandInputs;
}): BrandDraft => {
  const draft: BrandDraft = { label, sponsorName };
  for (const key of BRAND_COLOR_KEYS) {
    const color = inputs.colors[key];
    if (color) draft[key] = color;
  }
  if (inputs.borderRadius !== undefined) {
    draft.borderRadius = inputs.borderRadius.toString();
  }
  return draft;
};

const ratioText = (ratio: number) =>
  `${(Math.floor(ratio * 10) / 10).toFixed(1)}:1`;

export const checkBrandDraft = (draft: BrandDraft): BrandCheck => {
  const { inputs, errors } = parseBrandDraft(draft);
  const warnings: BrandCheck['warnings'] = {};

  const colors: Record<BrandColorKey, string> = {
    ...PARTICIPANT_HUB_SOLID_COLORS,
    ...inputs.colors,
  };
  const { primarySurface } = computeBrandTokens(inputs);

  const rows: {
    field: BrandColorKey;
    /** The draft colours the foreground and background come from. */
    uses: BrandColorKey[];
    foreground: string;
    background: string;
    min: number;
    message: (ratio: string) => string;
    severity: 'errors' | 'warnings';
  }[] = [
    ...(['background', 'surface'] as const).map(on => ({
      field: 'text' as const,
      uses: ['text' as const, on],
      foreground: colors.text,
      background: colors[on],
      min: MIN_TEXT_CONTRAST,
      message: (ratio: string) =>
        `Text on ${BRAND_COLOR_LABELS[on].toLowerCase()} is ${ratio}; needs 4.5:1`,
      severity: 'errors' as const,
    })),
    {
      field: 'text',
      uses: ['text', 'primary'],
      foreground: colors.text,
      background: primarySurface,
      min: MIN_TEXT_CONTRAST,
      message: ratio => `Text on the tinted surface is ${ratio}; needs 4.5:1`,
      severity: 'warnings',
    },
    {
      field: 'primary',
      uses: ['primary', 'surface'],
      foreground: colors.primary,
      background: colors.surface,
      min: MIN_TEXT_CONTRAST,
      message: ratio =>
        `Primary on surface is ${ratio}; text and outlined buttons need 4.5:1`,
      severity: 'warnings',
    },
    {
      field: 'primary',
      uses: ['primary'],
      foreground: colors.primary,
      background: primarySurface,
      min: MIN_TEXT_CONTRAST,
      message: ratio =>
        `Primary on the tinted surface is ${ratio}; primary text on it needs 4.5:1`,
      severity: 'warnings',
    },
    {
      field: 'primary',
      uses: ['primary', 'background'],
      foreground: colors.primary,
      background: colors.background,
      min: GRAPHIC_MIN_CONTRAST,
      message: ratio =>
        `Primary on page background is ${ratio}; controls need 3:1`,
      severity: 'warnings',
    },
    {
      field: 'secondary',
      uses: ['secondary', 'surface'],
      foreground: colors.secondary,
      background: colors.surface,
      min: MIN_TEXT_CONTRAST,
      message: ratio => `Secondary on surface is ${ratio}; text needs 4.5:1`,
      severity: 'warnings',
    },
    ...(['primary', 'secondary'] as const).map(field => ({
      field,
      uses: [field],
      foreground: '#ffffff',
      background: colors[field],
      min: MIN_TEXT_CONTRAST,
      message: (ratio: string) =>
        `White button text on ${field} is ${ratio}; buttons use black text. A darker ${field} keeps white text`,
      severity: 'warnings' as const,
    })),
  ];

  const malformed = new Set(Object.keys(errors));
  for (const row of rows) {
    if (row.uses.some(field => malformed.has(field))) continue;
    const ratio = getContrastRatio(row.foreground, row.background);
    if (ratio >= row.min) continue;
    const target = row.severity === 'errors' ? errors : warnings;
    const message = row.message(ratioText(ratio));
    const previous = target[row.field];
    target[row.field] = previous ? `${previous}. ${message}` : message;
  }

  let textTooLight = false;
  for (const on of ['background', 'surface'] as const) {
    if (malformed.has('text') || malformed.has(on)) continue;
    if (getLuminance(colors.text) <= getLuminance(colors[on])) continue;
    if (inputs.colors[on]) errors[on] = DARK_BRAND_ERROR;
    else textTooLight = true;
  }
  if (textTooLight) {
    errors.text = errors.text
      ? `${errors.text}. ${LIGHT_TEXT_ERROR}`
      : LIGHT_TEXT_ERROR;
  }

  return { errors, warnings };
};
